import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, MessageCircle, ArrowLeft, ChefHat } from 'lucide-react';
import { Button } from '../components/ui/button';

const SMASHED_CAKE_IMAGE = "https://images.unsplash.com/photo-1594247252698-1eb1802f5236?w=800&q=80";

export default function NotFoundPage() {
  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-4 py-12">
      <div className="max-w-2xl w-full text-center">
        {/* Animated 404 Number */}
        <motion.div
          initial={{ scale: 0, rotate: -10 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
          className="mb-6"
        >
          <span className="font-['Playfair_Display'] text-[120px] sm:text-[180px] font-bold text-[#8D6E63]/20 leading-none select-none">
            404
          </span>
        </motion.div>

        {/* Smashed Cake Image */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="relative mx-auto w-64 h-64 sm:w-80 sm:h-80 mb-8 -mt-20"
        >
          <div className="absolute inset-0 bg-[#8D6E63]/10 rounded-full blur-3xl" />
          <img
            src={SMASHED_CAKE_IMAGE}
            alt="Smashed cake"
            className="relative w-full h-full object-cover rounded-2xl shadow-2xl border-4 border-white"
          />
          {/* Chef Hat Decoration */}
          <motion.div
            initial={{ rotate: 0 }}
            animate={{ rotate: [0, -10, 10, -5, 0] }}
            transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
            className="absolute -top-6 -right-4 bg-white rounded-full p-3 shadow-lg"
          >
            <ChefHat className="w-8 h-8 text-[#8D6E63]" />
          </motion.div>
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-4"
        >
          Oops! Our whisk broke! 🥄
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="font-['Poppins'] text-lg text-stone-600 mb-8 max-w-md mx-auto"
        >
          This page wasn't found. Even the best recipes fail sometimes! 
          Don't worry, our bakers are already fixing it.
        </motion.p>

        {/* Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Button
            asChild
            size="lg"
            className="bg-[#1a1a1a] hover:bg-[#1a1a1a]/90 rounded-full px-8 font-['Poppins'] gap-2"
          >
            <Link to="/">
              <Home className="w-5 h-5" />
              Back to Home
            </Link>
          </Button>
          
          <Button
            asChild
            variant="outline"
            size="lg"
            className="rounded-full px-8 font-['Poppins'] border-[#8D6E63] text-[#8D6E63] hover:bg-[#8D6E63] hover:text-white gap-2"
          >
            <Link to="/contact">
              <MessageCircle className="w-5 h-5" />
              Contact Us
            </Link>
          </Button>
        </motion.div>

        {/* Back Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mt-8"
        >
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center gap-2 text-stone-500 hover:text-[#8D6E63] font-['Poppins'] text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Go back to previous page
          </button>
        </motion.div>

        {/* Fun Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-12 text-xs text-stone-400 font-['Poppins']"
        >
          Error 404 • Page not found • Mad Brothers Bakery
        </motion.p>
      </div>
    </div>
  );
}
